import React, { useEffect, useState } from "react";
import { Form, Upload, Button, Input, Drawer, Checkbox, Select, InputNumber, message } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { blogAlert, createBlogModal, editBlogBal, editBlogModal, blogRefresh, blogResponse, blogEditor } from "../../../states/blogs";
import { createBlogList, generateAIArticle, updateBlogList } from "../../../services/apiCalls";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { dummyRequest } from "../../../services/utilities";

const urlToFile = async (url, filename = `ai-article-${Date.now()}.png`) => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to download generated image: ${response.status}`);
  }
  const blob = await response.blob();
  return new File([blob], filename, { type: blob.type || "image/png" });
};

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    [{ font: [] }],
    [{ size: ['small', false, 'large', 'huge'] }],
    ['bold', 'italic', 'underline', 'strike', 'blockquote', 'code-block'],
    [{ color: [] }, { background: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
    [{ align: [] }],
    ['link', 'image', 'video'],
    ['clean'],
  ],
};

const quillFormats = [
  'header',
  'font',
  'size',
  'bold',
  'italic',
  'underline',
  'strike',
  'blockquote',
  'code-block',
  'color',
  'background',
  'list',
  'bullet',
  'indent',
  'align',
  'link',
  'image',
  'video',
];

// Wraps ReactQuill so Form.Item can control it safely. Guarantees `value`
// passed to Quill is always a string (never the Delta object emitted by
// Quill's onChange), which would otherwise trigger react-quill v1's
// "You are passing the `delta` object ... back as `value`" error.
const QuillField = ({ value, onChange, ...rest }) => {
  const safeValue = typeof value === "string" ? value : "";
  const handleChange = (content) => {
    if (typeof onChange === "function") {
      onChange(typeof content === "string" ? content : "");
    }
  };
  return (
    <ReactQuill
      {...rest}
      theme="snow"
      value={safeValue}
      onChange={handleChange}
      modules={quillModules}
      formats={quillFormats}
    />
  );
};

const BlogModal = ({ parentId }) => {
  const [loading, setLoading] = useState(false);
  const setShowAlert = useSetRecoilState(blogAlert);
  const setResponse = useSetRecoilState(blogResponse);
  const setRefresh = useSetRecoilState(blogRefresh);
  const [showModal, setShowModal] = useRecoilState(createBlogModal);
  const [file, setFile] = useState("");
  const [form] = Form.useForm();
  const [aiForm] = Form.useForm();
  const [editor, setEditor] = useState("");
  const [generating, setGenerating] = useState(false);
  const [aiImageUrl, setAiImageUrl] = useState("");
  const [aiImagePrompt, setAiImagePrompt] = useState("");
  const [aiImageStatus, setAiImageStatus] = useState("");

  const getBase64 = (img, callback) => {
    const reader = new FileReader();
    reader.addEventListener('load', () => callback(reader.result));
    reader.readAsDataURL(img);
  };
  const id = Number(parentId);
  const handleChange = info => {
    if (info.file.status === 'done') {
      getBase64(info.file.originFileObj, imageUrl => {
        setFile(info.file.originFileObj);
      });
    }
    if (info.file.status === 'removed') {
      setFile("");
    }
  };

  const getBusinessUnit = categoryId => {
    const businessUnits = {
      1: "wayabank",
      2: "wayagram",
      3: "wayaquick",
    };
    return businessUnits[categoryId];
  };

  const handleGenerateAIArticle = async () => {
    let values;
    try {
      values = await aiForm.validateFields();
    } catch (error) {
      return;
    }

    const businessUnit = getBusinessUnit(id);
    if (!businessUnit) {
      message.error("AI article generation is not available for this blog category.");
      return;
    }

    setGenerating(true);
    setAiImageUrl("");
    setAiImagePrompt("");
    setAiImageStatus("");

    try {
      const body = await generateAIArticle({
        category_id: id,
        business_unit: businessUnit,
        topic: values.topic,
        article_type: values.article_type,
        target_audience: values.target_audience,
        tone: values.tone,
        keywords: values.keywords,
        word_count: Number(values.word_count),
        language: "English",
        generate_image: values.generate_image || false,
      });

      if (!(body?.success || body?.status)) {
        throw new Error(body?.message || "Invalid AI response");
      }

      const generated = body?.data && typeof body.data === "object" ? body.data : body;
      const title = generated.title || body.title || "";
      const shortContent = generated.short_content || body.short_content || "";
      const content = generated.content || body.content || "";
      const imagePrompt = generated.image_prompt || body.image_prompt || "";
      const imageUrl = generated.image_url || body.image_url || "";

      if (!content) {
        throw new Error(body?.message || "AI response did not include article content.");
      }

      setEditor(content);

      form.setFieldsValue({
        title,
        short_content: shortContent,
        content: content,
        parent_id: id,
      });
      try {
        await form.validateFields(["content"]);
      } catch (error) {
        // Other fields may still be incomplete; only content needed revalidation.
      }

      setAiImageUrl(imageUrl);
      setAiImagePrompt(imagePrompt);
      setAiImageStatus(imageUrl ? "success" : "unavailable");
      message.success("Article generated. Review it below, then publish.");
    } catch (error) {
      message.error(error?.message || "AI article generation failed.");
    } finally {
      setGenerating(false);
    }
  };

  const handleSubmit = async (val) => {
    const date = new Date();
    setLoading(true);
    try {
      let imageFile = file;
      if (!imageFile && aiImageUrl) {
        try {
          imageFile = await urlToFile(aiImageUrl);
        } catch (error) {
          setAiImageStatus("download-failed");
          message.error("Could not download the AI-generated image. Please upload an image manually.");
          return;
        }
      }
      if (!imageFile) {
        message.error("Please generate or upload a blog image before publishing.");
        return;
      }

      const htmlContent = val.content || editor || "";

      const fd = new FormData();
      fd.append('title', val.title);
      fd.append('parent_id', String(val.parent_id ?? id));
      fd.append('short_content', val.short_content);
      fd.append('content', htmlContent);
      fd.append('file', imageFile);
      const res = await createBlogList(fd);
      if (res && res.status) {
        setResponse({ message: res.message, title: "success" });
        setShowAlert(true);
        setRefresh(date.toTimeString());
        setShowModal(false);
        setFile("");
        setEditor("");
        setAiImageUrl("");
        setAiImagePrompt("");
        setAiImageStatus("");
        form.resetFields();
        aiForm.resetFields();
      } else {
        const errMessage = (res && res.message) || "Failed to publish the blog post.";
        setResponse({ message: errMessage, title: "failed" });
        setShowAlert(true);
        message.error(errMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const onEditorChange = (content) => {
    setEditor(content);
    form.setFieldsValue({ content });
  };

  return (
    <Drawer
      title="Create New Blog"
      placement="right"
      width={720}
      closable={false}
      onClose={() => setShowModal(false)}
      visible={showModal}
    >
      <div className="card" style={{ overflow: 'visible' }}>
        <div className="card-body" style={{ overflow: 'visible' }}>
          <div className="mb-4 p-3 border rounded">
            <h6>Generate with AI</h6>
            <Form form={aiForm} layout="vertical">
              <Form.Item label="Topic" name="topic" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
              <Form.Item label="Article Type" name="article_type" initialValue="educational" rules={[{ required: true }]}>
                <Select>
                  <Select.Option value="educational">educational</Select.Option>
                  <Select.Option value="news">news</Select.Option>
                  <Select.Option value="announcement">announcement</Select.Option>
                  <Select.Option value="marketing">marketing</Select.Option>
                </Select>
              </Form.Item>
              <Form.Item label="Target Audience" name="target_audience" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
              <Form.Item label="Tone" name="tone" initialValue="professional" rules={[{ required: true }]}>
                <Select>
                  <Select.Option value="professional">professional</Select.Option>
                  <Select.Option value="friendly">friendly</Select.Option>
                  <Select.Option value="formal">formal</Select.Option>
                  <Select.Option value="conversational">conversational</Select.Option>
                </Select>
              </Form.Item>
              <Form.Item label="Keywords" name="keywords">
                <Input />
              </Form.Item>
              <Form.Item label="Word Count" name="word_count" initialValue={800} rules={[{ required: true }]}>
                <InputNumber min={300} max={3000} step={100} />
              </Form.Item>
              <Form.Item name="generate_image" valuePropName="checked">
                <Checkbox>Generate image</Checkbox>
              </Form.Item>
              <Form.Item>
                <Button type="primary" onClick={handleGenerateAIArticle} loading={generating}>
                  Generate with AI
                </Button>
              </Form.Item>
            </Form>
            {aiImageStatus === "success" && aiImageUrl && (
              <div className="mb-3">
                <p className="mb-2">AI-generated image (used as the blog image unless you upload one below)</p>
                <img src={aiImageUrl} alt="AI generated preview" style={{ width: "100%", maxHeight: 220, objectFit: "cover" }} />
              </div>
            )}
            {aiImageStatus === "unavailable" && (
              <p className="text-warning mb-3">AI image unavailable — upload a blog image manually below.</p>
            )}
            {aiImageStatus === "download-failed" && (
              <p className="text-danger mb-3">Image download failed — upload a blog image manually below.</p>
            )}
            {aiImagePrompt && (
              <div>
                <p className="mb-2">Image prompt</p>
                <Input.TextArea value={aiImagePrompt} readOnly autoSize />
              </div>
            )}
          </div>
          <Form form={form} layout="vertical" onFinish={handleSubmit}>
            <Form.Item label="Blog Title" name="title" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item label="Blog Description" name="short_content" rules={[{ required: true }]}>
              <Input.TextArea />
            </Form.Item>
            <Form.Item hidden initialValue={id} name="parent_id" rules={[{ required: true }]}>
              <Input hidden />
            </Form.Item>
            <Form.Item label="Blog Content" name="content" rules={[{ required: true }]}>
              <QuillField placeholder="Write your blog content here..." />
            </Form.Item>
            <Form.Item >
              <Upload onChange={handleChange} customRequest={dummyRequest} maxCount={1}>
                <Button icon={<InboxOutlined />}>Upload Blog Image</Button>
              </Upload>
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={loading}>
                Publish Article
              </Button>
            </Form.Item>
          </Form>
        </div>
      </div>
    </Drawer>
  );
};
export default BlogModal;

export const EditModal = () => {
  const [loading, setLoading] = useState(false);
  const setShowAlert = useSetRecoilState(blogAlert);
  const setResponse = useSetRecoilState(blogResponse);
  const setRefresh = useSetRecoilState(blogRefresh);
  const [showModal, setShowModal] = useRecoilState(editBlogModal);
  const value = useRecoilValue(editBlogBal);
  const [editor, setEditor] = useRecoilState(blogEditor);
  const [file, setFile] = useState("");
  const [form] = Form.useForm();

  useEffect(() => {
    if (showModal) {
      const htmlContent = value.content || "";
      form.setFieldsValue({
        title: value.title,
        short_content: value.short_content,
        id: value.id,
        image_url: value.image_url,
        content: htmlContent,
      });
      setEditor(htmlContent);
    }
  }, [showModal, value, form, setEditor]);

  const getBase64 = (img, callback) => {
    const reader = new FileReader();
    reader.addEventListener('load', () => callback(reader.result));
    reader.readAsDataURL(img);
  };

  const handleChange = info => {
    if (info.file.status === 'done') {
      getBase64(info.file.originFileObj, imageUrl => {
        setFile(info.file.originFileObj);
      });
    }
  };

  const handleSubmit = async (val) => {
    const date = new Date();
    setLoading(true);
    const htmlContent = val.content || (typeof editor === 'string' ? editor : "") || "";
    const fd = new FormData();
    fd.append('title', val.title);
    fd.append('short_content', val.short_content);
    fd.append('content', htmlContent);
    fd.append('blog_image', file);
    fd.append('id', val.id);
    fd.append('image_url', val.image_url);
    const res = await updateBlogList({ data: fd, id: val.id });
    if (res && res.status) {
      setResponse({ message: res.message, title: "success" });
      setShowAlert(true);
      setRefresh(date.toTimeString());
      setShowModal(false);
      setFile("");
      setEditor("");
      form.resetFields();
    } else {
      setResponse({ message: (res && res.message) || res, title: "failed" });
      setShowAlert(true);
    }
    setLoading(false);
  };

  const onEditorChange = (content) => {
    setEditor(content);
    form.setFieldsValue({ content });
  };

  return (
    <Drawer
      title="Edit Blog Post"
      placement="right"
      width={720}
      closable={false}
      onClose={() => setShowModal(false)}
      visible={showModal}
    >
      <div className="card" style={{ overflow: 'visible' }}>
        <div className="card-body" style={{ overflow: 'visible' }}>
          <Form form={form} layout="vertical" onFinish={handleSubmit} initialValues={{ title: value.title, short_content: value.short_content, id: value.id, image_url: value.image_url }}>
            <Form.Item label="Blog Title" name="title" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item hidden name="id">
              <Input />
            </Form.Item>
            <Form.Item label="Blog Description" name="short_content" rules={[{ required: true }]}>
              <Input.TextArea />
            </Form.Item>
            <Form.Item label="Blog Content" name="content" rules={[{ required: true }]}>
              <QuillField placeholder="Write your blog content here..." />
            </Form.Item>
            <Form.Item >
              <Upload onChange={handleChange} customRequest={dummyRequest} maxCount={1}>
                <Button icon={<InboxOutlined />}>Upload Blog Image</Button>
              </Upload>
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={loading}>
                Update
              </Button>
            </Form.Item>
          </Form>
        </div>
      </div>
    </Drawer>
  );
};
